import React, { useState } from 'react';
import {
  X,
  User,
  Calendar,
  CheckCircle,
  PencilSimple,
  Trash,
} from '@phosphor-icons/react';
import { AnalyzerWeeklyPrompt } from '@Models/types';
import { SetDefaultConfirmationModal } from './SetDefaultConfirmationModal';

interface ViewPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  prompt: AnalyzerWeeklyPrompt | null;
  isAdmin?: boolean;
  onEdit?: (prompt: AnalyzerWeeklyPrompt) => void;
  onDelete?: (prompt: AnalyzerWeeklyPrompt) => void;
  onSetDefault?: (prompt: AnalyzerWeeklyPrompt) => void;
  currentDefaultPrompt?: AnalyzerWeeklyPrompt | null;
}

export const ViewPromptModal: React.FC<ViewPromptModalProps> = ({
  isOpen,
  onClose,
  prompt,
  isAdmin = false,
  onEdit,
  onDelete,
  onSetDefault,
  currentDefaultPrompt,
}) => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  if (!isOpen || !prompt) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-full max-w-3xl rounded-lg bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold text-gray-900">
              {prompt.promptTitle}
            </h2>
            {prompt.isDefault && (
              <span className="flex items-center gap-1 rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
                <CheckCircle size={12} weight="fill" />
                Default
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <X size={20} />
          </button>
        </div>

        <div className="mb-4 flex items-center gap-4 text-sm text-gray-600">
          <div className="flex items-center gap-1">
            <User size={16} />
            <span>Created by {prompt.authorEmail}</span>
          </div>
          <div className="flex items-center gap-1">
            <Calendar size={16} />
            <span>{new Date(prompt.dateCreated).toLocaleString()}</span>
          </div>
        </div>

        <div className="mb-6">
          <h3 className="mb-2 text-sm font-medium text-gray-700">
            Prompt Content
          </h3>
          <div className="max-h-96 overflow-y-auto rounded-md border border-gray-300 bg-gray-50 p-4">
            <pre className="whitespace-pre-wrap text-sm text-gray-800">
              {prompt.promptString}
            </pre>
          </div>
        </div>

        <div className="flex justify-between">
          <div className="flex gap-3">
            {isAdmin && onEdit && (
              <button
                onClick={() => {
                  onEdit(prompt);
                }}
                className="flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700"
              >
                <PencilSimple size={16} />
                Edit
              </button>
            )}
            {isAdmin && onSetDefault && !prompt.isDefault && (
              <button
                onClick={() => setShowConfirmation(true)}
                className="flex items-center gap-2 rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700"
              >
                <CheckCircle size={16} />
                Set as Default
              </button>
            )}
            {isAdmin && onDelete && !prompt.isDefault && (
              <button
                onClick={() => {
                  onDelete(prompt);
                }}
                className="flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700"
              >
                <Trash size={16} />
                Delete
              </button>
            )}
          </div>
          <button
            onClick={onClose}
            className="rounded-md bg-gray-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>

      <SetDefaultConfirmationModal
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={(prompt) => {
          if (onSetDefault) {
            onSetDefault(prompt);
            onClose();
          }
        }}
        prompt={prompt}
        currentDefaultPrompt={currentDefaultPrompt}
      />
    </div>
  );
};
